import React, { useState, useEffect } from 'react';
import RadarCompetenciasChart from '../components/RadarCompetenciasChart';
import CompetenceLineChart from '../components/CompetenceLineChart';
import ChatComponent from '../components/ChatComponent';
import { getQuadrantInfo, quadrantColors } from '../utils/dashboardUtils';

const API_CONFIG = {
  baseUrl: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v1', // Usar variável de ambiente com fallback
  endpoints: {
    pessoas: '/pessoas',
    nineBox: '/nine-box',
    radarCompetencias: '/radar-competencias',
    heatmapCompetencias: '/heatmap-competencias',
    distribuicaoScarf: '/distribuicao-scarf',
    competenciasHistorico: '/competencias-historico',
  }
};

const IndividualProfileDashboard = ({ apiCall, activeTab }) => {
  // Estados para Perfil Individual
  const [pessoas, setPessoas] = useState([]); // Será carregado uma vez
  const [selectedPersonProfileId, setSelectedPersonProfileId] = useState(null);
  const [selectedPersonProfileName, setSelectedPersonProfileName] = useState('');
  const [personSearchText, setPersonSearchText] = useState('');
  const [filteredPessoasProfile, setFilteredPessoasProfile] = useState([]);
  const [showPersonDropdown, setShowPersonDropdown] = useState(false);

  // Estados para dados dos KPIs do Perfil Individual
  const [kpiProfileData, setKpiProfileData] = useState({
    nineBox: null,
    competenciaMaiorValor: null,
    motivadorScarf: null,
    radarCompetencias: null,
    competenceHistory: null,
  });
  const [kpiProfileLoading, setKpiProfileLoading] = useState(false);
  const [kpiProfileError, setKpiProfileError] = useState(null);

  // Carregar lista de pessoas apenas uma vez para o filtro de perfil individual
  useEffect(() => {
    const loadPessoas = async () => {
      const pessoasData = await apiCall(API_CONFIG.endpoints.pessoas);
      setPessoas(pessoasData || []);
    };
    loadPessoas();
  }, [apiCall]); // eslint-disable-next-line react-hooks/exhaustive-deps

  // Filtrar a lista de pessoas para o filtro de perfil individual (com debounce)
  useEffect(() => {
    const debounceFilter = setTimeout(() => {
      if (personSearchText.length > 0) {
        setFilteredPessoasProfile(
          pessoas.filter((pessoa) =>
            pessoa.pes_nm.toLowerCase().includes(personSearchText.toLowerCase())
          )
        );
      } else {
        setFilteredPessoasProfile(pessoas);
      }
    }, 300);

    return () => clearTimeout(debounceFilter);
  }, [personSearchText, pessoas]);

  // Carregar dados dos KPIs do Perfil Individual quando selectedPersonProfileId mudar ou aba mudar
  useEffect(() => {
    const loadKpiProfileData = async () => {
      if (activeTab !== 'dashboard2' || !selectedPersonProfileId) {
        setKpiProfileData({
          nineBox: null,
          competenciaMaiorValor: null,
          motivadorScarf: null,
          radarCompetencias: null,
          competenceHistory: null,
        });
        setKpiProfileError(null);
        return;
      }

      setKpiProfileLoading(true);
      setKpiProfileError(null);

      try {
        const params = { pes_id: selectedPersonProfileId };

        const [nineBox, heatmap, scarf, radarCompetencias, competenceHistory] = await Promise.all([
          apiCall(API_CONFIG.endpoints.nineBox, params),
          apiCall(API_CONFIG.endpoints.heatmapCompetencias, params),
          apiCall(API_CONFIG.endpoints.distribuicaoScarf, params),
          apiCall(API_CONFIG.endpoints.radarCompetencias, params),
          apiCall(API_CONFIG.endpoints.competenciasHistorico, params),
        ]);

        const processedNineBox = nineBox.length > 0 ? getQuadrantInfo(nineBox[0].desempenho, nineBox[0].potencial) : null;

        let competenciaMaiorValor = null;
        if (heatmap.length > 0) {
          const maxCompetencia = heatmap.reduce((prev, current) => {
            return (prev.valor_medio_analise > current.valor_medio_analise) ? prev : current;
          });
          const nomeCompetencia = maxCompetencia.descricao_caracteristica
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/\s+/g, ' ')
            .trim()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
          competenciaMaiorValor = {
            nome: nomeCompetencia,
            valor: parseFloat(maxCompetencia.valor_medio_analise).toFixed(2)
          };
        }

        let motivadorScarf = null;
        if (scarf.length > 0) {
          const totalScarf = scarf.reduce((sum, item) => sum + item.contagem, 0);
          const maxMotivador = scarf.reduce((prev, current) => {
            return (prev.contagem > current.contagem) ? prev : current;
          });
          motivadorScarf = {
            nome: maxMotivador.codigo_scarf,
            probabilidade: ((maxMotivador.contagem / totalScarf) * 100).toFixed(0) + '%'
          };
        }

        setKpiProfileData({
          nineBox: processedNineBox,
          competenciaMaiorValor: competenciaMaiorValor,
          motivadorScarf: motivadorScarf,
          radarCompetencias: radarCompetencias,
          competenceHistory: competenceHistory,
        });

      } catch (error) {
        console.error("Erro ao carregar dados do perfil individual:", error);
        setKpiProfileError("Não foi possível carregar os dados do perfil.");
        setKpiProfileData({
          nineBox: null,
          competenciaMaiorValor: null,
          motivadorScarf: null,
          radarCompetencias: null,
          competenceHistory: null,
        });
      } finally {
        setKpiProfileLoading(false);
      }
    };

    loadKpiProfileData();
  }, [selectedPersonProfileId, activeTab, apiCall]); // eslint-disable-next-line react-hooks/exhaustive-deps

  // Fechar o dropdown de busca de pessoa ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      const searchContainer = document.getElementById('person-search-container');
      if (searchContainer && !searchContainer.contains(event.target)) {
        setShowPersonDropdown(false);
      }
    };

    if (showPersonDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPersonDropdown]);

  return (
    <div className="p-4">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Perfil Individual</h2>

      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <label htmlFor="person-search" className="block text-sm font-medium text-gray-700 mb-2">Buscar Pessoa</label>
        <div className="relative" id="person-search-container">
          <input
            id="person-search"
            type="text"
            value={personSearchText}
            onChange={(e) => {
              setPersonSearchText(e.target.value);
              setShowPersonDropdown(true);
              setSelectedPersonProfileId(null);
              setSelectedPersonProfileName('');
            }}
            onFocus={() => setShowPersonDropdown(true)}
            aria-label="Buscar por nome da pessoa"
            placeholder="Digite o nome da pessoa..."
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10"
          />

          {personSearchText && (
            <button
              onClick={() => {
                setPersonSearchText('');
                setFilteredPessoasProfile([]);
                setSelectedPersonProfileId(null);
                setSelectedPersonProfileName('');
                setShowPersonDropdown(false);
              }}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500 hover:text-gray-700"
              aria-label="Limpar filtro de busca"
            >
              ✕
            </button>
          )}

          {showPersonDropdown && filteredPessoasProfile && (
            <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto mt-1">
              {filteredPessoasProfile.map((pessoa) => (
                <li
                  key={pessoa.pes_id}
                  onClick={() => {
                    setSelectedPersonProfileId(pessoa.pes_id);
                    setSelectedPersonProfileName(pessoa.pes_nm);
                    setPersonSearchText(pessoa.pes_nm);
                    setShowPersonDropdown(false);
                  }}
                  className="p-2 cursor-pointer hover:bg-blue-100"
                >
                  {pessoa.pes_nm}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {selectedPersonProfileId && (
        <div className="mt-8">
          {kpiProfileLoading && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Carregando dados do perfil...</p>
            </div>
          )}

          {kpiProfileError && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
              <strong className="font-bold">Erro:</strong>
              <span className="block sm:inline">{kpiProfileError}</span>
            </div>
          )}

          {!kpiProfileLoading && !kpiProfileError && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Cartão 1: Nome da Pessoa */}
                <div className="bg-white p-4 rounded-lg shadow flex items-center">
                  <div className="mr-4 text-4xl">👤</div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-800">{selectedPersonProfileName}</h4>
                  </div>
                </div>

                {/* Cartão 2: Classificação Nine Box */}
                <div className="bg-white p-4 rounded-lg shadow"
                      style={kpiProfileData.nineBox ? { backgroundColor: quadrantColors[kpiProfileData.nineBox.key], color: 'white' } : {}}>
                  <h4 className="text-lg font-semibold mb-2">Classificação Nine Box</h4>
                  {kpiProfileData.nineBox ? (
                    <>
                      <p className="text-2xl font-bold">
                        {kpiProfileData.nineBox.name}
                      </p>
                    </>
                  ) : (
                    <p className="text-gray-500">Sem dados de Nine Box.</p>
                  )}
                </div>

                {/* Cartão 3: Competência de Maior Valor */}
                <div className="bg-white p-4 rounded-lg shadow">
                  <h4 className="text-lg font-semibold mb-2">Competência Destaque</h4>
                  {kpiProfileData.competenciaMaiorValor ? (
                    <>
                      <p className="text-2xl font-bold text-blue-600">{kpiProfileData.competenciaMaiorValor.nome}</p>
                      <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2">
                        <div
                          className="bg-blue-600 h-2.5 rounded-full"
                          style={{ width: `${(parseFloat(kpiProfileData.competenciaMaiorValor.valor) / 5) * 100}%` }}
                        ></div>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">Valor: {kpiProfileData.competenciaMaiorValor.valor}</p>
                    </>
                  ) : (
                    <p className="text-gray-500">Sem dados de competência.</p>
                  )}
                </div>

                {/* Cartão 4: Maior Motivador SCARF */}
                <div className="bg-white p-4 rounded-lg shadow">
                  <h4 className="text-lg font-semibold mb-2">SCARF</h4>
                  {kpiProfileData.motivadorScarf ? (
                    <>
                      <p className="text-2xl font-bold text-purple-600">{kpiProfileData.motivadorScarf.nome}</p>
                      <p className="text-sm text-gray-600 mt-1">Probabilidade: {kpiProfileData.motivadorScarf.probabilidade}</p>
                    </>
                  ) : (
                    <p className="text-gray-500">Sem dados de SCARF.</p>
                  )}
                </div>
              </div>
              {/* Componente de Radar de Competências */}
              {kpiProfileData.radarCompetencias && (
                <div className="mt-6">
                  <RadarCompetenciasChart radarData={kpiProfileData.radarCompetencias} />
                </div>
              )}
              {/* Componente de Gráfico de Linha de Histórico de Competências */}
              {kpiProfileData.competenceHistory && kpiProfileData.competenceHistory.length > 0 && (
                <div className="mt-6">
                  <CompetenceLineChart pesId={selectedPersonProfileId} competenceHistoryData={kpiProfileData.competenceHistory} />
                </div>
              )}
              {/* Componente de Chat com LLM */}
              <div className="mt-6">
                <ChatComponent selectedPersonName={selectedPersonProfileName} />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default IndividualProfileDashboard;